import { Column, TablePaginated, TablePaginatedOptions, Tooltip } from '@acceso/design-system';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from '@/components/ConfirmationModal';
import { RenderActionButtons, RenderActionButtonsTooltips } from '@/components/RenderActionButtons';
import { StatusChipInTable } from '@/components/StatusChipInTable';
import { TableHeaderByStatus } from '@/components/TableHeaderByStatus';
import { TableRow } from '@/components/TableRow';
import { TableRowInfo } from '@/components/TableRowInfo/TableRowInfo';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { CHANNEL_PROVIDER_TO_TRANSLATION_KEY } from '@/features/broadcasters/constants';
import { CHANNEL_MEDIA_TYPE_TO_ICON } from '@/features/broadcasters/constants/icons';
import { useChannelMediaTypeOptions } from '@/features/broadcasters/hooks/useChannelMediaTypeOptions';
import { useChannelOptions } from '@/features/broadcasters/hooks/useChannelOptions';
import { useChannelProviderOptions } from '@/features/broadcasters/hooks/useChannelProviderOptions';
import { ChannelMediaTypeEnum, ChannelProviderEnum } from '@/features/broadcasters/types';
import { useProductionCenters } from '@/features/production-center/api/get-production-centers';
import { useProductionCenterOptions } from '@/features/production-center/hooks/useProductionCenterOptions';
import { formatDateCustom } from '@/helpers/format-date';
import { useGetDatetimeRangeFormatted } from '@/hooks/useDatetimeRangeFormatted';
import { routerManager } from '@/routes/app-routes';
import { MediaManagerRouteEnum } from '@/routes/constants/MediaManagerRouteEnum';
import { PaginatedTableProps } from '@/types/table';
import { withAppTzDateFilter } from '@/utils/app-tz-date-range';
import { createTableFilterOptions } from '@/utils/table-filters';

import { useUpdateChannelRecordingTaskAction } from '../../api/update-channel-recording-task-action';
import { CHANNEL_RECORDING_TASK_TYPE_TO_COLUMN_LABEL } from '../../constants';
import { useChannelRecordingTaskStateOptions } from '../../hooks/useChannelRecordingTaskStateOptions';
import { useChannelRecordingTaskTypeOptions } from '../../hooks/useChannelRecordingTaskTypeOptions';
import {
  ChannelRecordingTaskActionEnum,
  ChannelRecordingTaskAggregations,
  ChannelRecordingTaskStateEnum,
  ChannelRecordingTaskType,
  ChannelRecordingTaskTypeEnum,
} from '../../types';
import { getChannelRecordingTaskSpecialClassNames } from '../../utils';
import { useChannelRecordingTaskFilters } from '../RecorderDetailCard/channel-recording-task-filters';

type RecorderTableProps = PaginatedTableProps<
  ChannelRecordingTaskType,
  ChannelRecordingTaskAggregations
>;

export function RecorderTable({
  items,
  totalItems,
  onPreview,
  onCancel,
  activeItem,
  isFetching,
  aggregation,
}: RecorderTableProps) {
  const { t } = useTranslation();
  const { data: { data: channels = [] } = {} } = useChannels({
    params: { isParentChannel: false },
  });
  const channelsById = useMemo(
    () => new Map(channels.map(channel => [channel.id, channel])),
    [channels]
  );
  const {
    filters: tableFilters,
    paginationValues,
    setFilters,
    setPage,
    setRows,
    setSortBy,
    setSearchQuery,
  } = useChannelRecordingTaskFilters();

  const [relaunchTarget, setRelaunchTarget] = useState<{
    item: ChannelRecordingTaskType;
    action: ChannelRecordingTaskActionEnum;
  } | null>(null);

  const channelMediaTypeOptions = useChannelMediaTypeOptions();
  const channelOptions = useChannelOptions({ isParentChannel: false });
  const channelProviderOptions = useChannelProviderOptions();
  const channelRecordingTaskTypeOptions = useChannelRecordingTaskTypeOptions();
  const channelRecordingTaskStateOptions = useChannelRecordingTaskStateOptions();
  const productionCenterOptions = useProductionCenterOptions();
  const getDatetimeRangeFormatted = useGetDatetimeRangeFormatted();
  const { mutate: updateChannelRecordingTaskAction } = useUpdateChannelRecordingTaskAction();
  const productionCenterIds = new Set(items.map(item => item.production_center_id));
  const { data: { data: productionCenters = [] } = {} } = useProductionCenters({
    params: { idIn: [...productionCenterIds] },
    // The API reads an empty `id__in` as "no filter" and returns every centre.
    config: { enabled: productionCenterIds.size > 0 },
  });

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    dateFilter: withAppTzDateFilter({
      initialValue: {
        start: new Date(tableFilters.createdAtGte ?? ''),
        end: new Date(tableFilters.createdAtLte ?? ''),
      },
      name: 'created_at',
      placeholder: t('Fecha y hora de emisión'),
      showHours: true,
      onChange: ({ start, end }) => {
        setFilters({
          createdAtGte: start?.toISOString(),
          createdAtLte: end?.toISOString(),
        });
      },
    }),
    filters: [
      {
        name: 'channel__media_type',
        placeholder: t('Soporte'),
        options: createTableFilterOptions({
          options: channelMediaTypeOptions,
          allOptionLabel: t('Todos los soportes'),
        }),
        onChange: value => {
          setFilters({
            channelMediaTypeIn: value ? [value as unknown as ChannelMediaTypeEnum] : undefined,
          });
        },
      },
      {
        name: 'provider',
        placeholder: t('Proveedor'),
        options: createTableFilterOptions({
          options: channelProviderOptions,
          allOptionLabel: t('Todos los proveedores'),
        }),
        onChange: value => {
          setFilters({
            channelProviderIn: value ? [value as unknown as ChannelProviderEnum] : undefined,
          });
        },
      },
      {
        name: 'channel_id',
        placeholder: t('Emisora'),
        options: createTableFilterOptions({
          options: channelOptions,
          allOptionLabel: t('Todas las emisoras'),
        }),
        onChange: value => {
          setFilters({
            channelIdIn: value ? [value as unknown as string] : undefined,
          });
        },
      },
      {
        name: 'recording_task_type',
        placeholder: t('Tipo'),
        options: createTableFilterOptions({
          options: channelRecordingTaskTypeOptions,
          allOptionLabel: t('Todos los tipos'),
        }),
        onChange: value => {
          setFilters({
            recordingTaskTypeIn: value
              ? [value as unknown as ChannelRecordingTaskTypeEnum]
              : undefined,
          });
        },
      },
      {
        name: 'state',
        placeholder: t('Estado'),
        options: createTableFilterOptions({
          options: channelRecordingTaskStateOptions,
          allOptionLabel: t('Todos los estados'),
        }),
        onChange: value => {
          setFilters({
            stateIn: value ? [value as unknown as ChannelRecordingTaskStateEnum] : undefined,
          });
        },
      },
      {
        name: 'production_center',
        placeholder: t('Centros de producción'),
        options: createTableFilterOptions({
          options: productionCenterOptions,
          allOptionLabel: t('Todos los centros de producción'),
        }),
        onChange: value => {
          setFilters({
            productionCenterIdIn: value ? [value as unknown as string] : undefined,
          });
        },
      },
    ],
    searchBarPlaceholder: t('Buscar por descripción o nombre de emisora'),
  };

  const columns: Column<ChannelRecordingTaskType>[] = [
    {
      id: 'channel',
      title: t('Emisora'),
      render: item => {
        const channel = channelsById.get(item.channel_id);
        const Icon = channel ? CHANNEL_MEDIA_TYPE_TO_ICON[channel.media_type] : undefined;

        return <TableRowInfo title={channel?.name} description={channel?.short_name} icon={Icon} />;
      },
    },
    {
      id: 'provider',
      title: t('Proveedor'),
      render: item => {
        const channel = channelsById.get(item.channel_id);
        const label = channel?.provider
          ? t(CHANNEL_PROVIDER_TO_TRANSLATION_KEY[channel.provider])
          : '—';
        return <TableRow content={label} />;
      },
    },
    {
      id: 'channel_id',
      title: t('ID Emisora'),
      render: item => {
        const channel = channelsById.get(item.channel_id);

        return <TableRow content={channel?.id} />;
      },
    },
    {
      id: 'description',
      title: t('Descripción'),
      render: item => {
        return <TableRow content={item.description ?? ''} />;
      },
    },
    {
      id: 'production_center',
      title: t('Centro de producción'),
      render: item => {
        const productionCenter = productionCenters.find(
          productionCenter => productionCenter.id === item.production_center_id
        );
        return <TableRow content={productionCenter?.name} />;
      },
    },
    {
      id: 'recording_task_type',
      title: t('Tipo Grabación'),
      render: item => {
        const label = CHANNEL_RECORDING_TASK_TYPE_TO_COLUMN_LABEL[item.recording_task_type];
        return <TableRow content={t(label)} />;
      },
    },
    {
      id: 'schedule',
      title: t('Programación'),
      render: item => {
        return (
          <TableRow title={getDatetimeRangeFormatted(item.start_datetime, item.end_datetime)} />
        );
      },
    },
    {
      id: 'state',
      title: t('Estado'),
      sortable: true,
      valueFn: item => item.state,
      render: item => {
        const timestamp = item.updated_at || item.created_at;
        const help = formatDateCustom({ input: timestamp, format: 'dd/MM/yyyy HH:mm:ss' });
        return (
          <Tooltip help={help} grow>
            <StatusChipInTable status={item.state} />
          </Tooltip>
        );
      },
    },
    {
      id: 'actions',
      title: '',
      className: 'items-end',
      width: '5%',
      render: item => {
        const tooltips: RenderActionButtonsTooltips = {
          pause: t('Pausar grabación'),
          segments: t('Consultar ficheros'),
          update: t('Editar proceso de grabación'),
          preview: t('Ver último archivo generado en la grabación'),
        };

        const handlePreview = () => {
          onPreview?.(item);
        };

        const handleSegments = () => {
          routerManager.openNewTab({
            name: MediaManagerRouteEnum.SEGMENTS,
            searchParams: {
              // Franjas filters by gte / lte (the calendar range); a task with no end keeps its default end.
              gte: item.start_datetime,
              ...(item.end_datetime ? { lte: item.end_datetime } : {}),
              broadcasters: item.channel_id,
            },
          });
        };

        const handlePause = () => {
          updateChannelRecordingTaskAction({
            id: item.id,
            action: ChannelRecordingTaskActionEnum.PAUSE,
          });
        };

        const handleCancel = () => {
          onCancel?.(item);
        };

        const openRelaunchModal = (action: ChannelRecordingTaskActionEnum) => {
          setRelaunchTarget({ item, action });
        };

        if (item.state === ChannelRecordingTaskStateEnum.RUNNING) {
          return (
            <RenderActionButtons
              item={item}
              tooltips={tooltips}
              onPause={handlePause}
              onCancel={handleCancel}
              onPreview={handlePreview}
              onSegments={handleSegments}
              onRelaunch={() => openRelaunchModal(ChannelRecordingTaskActionEnum.RESTART)}
            />
          );
        }

        if (item.state === ChannelRecordingTaskStateEnum.PAUSED) {
          return (
            <RenderActionButtons
              item={item}
              tooltips={tooltips}
              onRelaunch={() => openRelaunchModal(ChannelRecordingTaskActionEnum.UNPAUSE)}
              onSegments={handleSegments}
              onCancel={handleCancel}
            />
          );
        }

        if (item.state === ChannelRecordingTaskStateEnum.PENDING) {
          return <RenderActionButtons item={item} tooltips={tooltips} onCancel={handleCancel} />;
        }

        if (item.state === ChannelRecordingTaskStateEnum.TIMEOUT) {
          return (
            <RenderActionButtons
              item={item}
              tooltips={tooltips}
              onRelaunch={() => openRelaunchModal(ChannelRecordingTaskActionEnum.RESTART)}
              onCancel={handleCancel}
            />
          );
        }

        if (item.state === ChannelRecordingTaskStateEnum.ERROR) {
          return (
            <RenderActionButtons
              item={item}
              tooltips={tooltips}
              onRelaunch={() => openRelaunchModal(ChannelRecordingTaskActionEnum.RESTART)}
              onCancel={handleCancel}
              onSegments={handleSegments}
              onPreview={handlePreview}
            />
          );
        }

        if (item.state === ChannelRecordingTaskStateEnum.COMPLETED) {
          return (
            <RenderActionButtons
              item={item}
              tooltips={tooltips}
              onPreview={handlePreview}
              onSegments={handleSegments}
            />
          );
        }

        return null;
      },
    },
  ];

  const relaunchChannel = relaunchTarget
    ? channelsById.get(relaunchTarget.item.channel_id)
    : undefined;

  return (
    <div className="flex flex-col gap-2">
      <TablePaginated<ChannelRecordingTaskType>
        data={items}
        totalItems={totalItems}
        preBody={<TableHeaderByStatus aggregation={aggregation} />}
        options={options}
        columns={columns}
        activeItem={activeItem}
        ignoreEmpty
        isFetching={isFetching}
        getSpecialClassNames={getChannelRecordingTaskSpecialClassNames}
      />
      <ConfirmationModal
        isOpen={relaunchTarget !== null}
        onClose={() => setRelaunchTarget(null)}
        onAction={() => {
          if (relaunchTarget) {
            updateChannelRecordingTaskAction({
              id: relaunchTarget.item.id,
              action: relaunchTarget.action,
            });
          }
          setRelaunchTarget(null);
        }}
        heading={t('Relanzar proceso de grabación')}
        title={
          relaunchChannel?.name
            ? t('¿Quieres relanzar {{channelName}}?', { channelName: relaunchChannel.name })
            : t('¿Quieres relanzar este proceso de grabación?')
        }
        actionLabel={t('Relanzar proceso')}
        cancelLabel={t('Cancelar')}
      />
    </div>
  );
}
